import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProduct, { useProductForm } from '@hooks/useProduct'
import useProductLines from '@hooks/useProductLines'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateProductModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProduct: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateProductModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createProduct } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const product = digObject(modalPayload, 'product', {})

  const { productLines } = useProductLines({ performHttpRequests: true })

  const {
    callbacks: {
      createProduct: createFn,
    },
    creating,
  } = useProduct(product)

  const {
    entityState,
    entityState: {
      description,
      name,
      productLineId,
    },
    errors,
    handlers,
    saveEnabled,
  } = useProductForm(product, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const createProductPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Product Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Create" titleSecondLine="Product" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Name" errorMessage={errors.name}>
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>

          <Form.Field label="* Description" errorMessage={errors.description} marginTop="large">
            <textarea
              name="description"
              value={description}
              style={{ height: 120, resize: 'vertical' }}
              {...handlers}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.productLineId} label="* Product Line" marginTop="large">
            <select name="productLineId" value={productLineId} {...handlers}>
              <option value="">Please Select...</option>
              {productLines.map(productLine => (
                <option key={productLine.id} value={productLine.name}>{productLine.displayName}</option>
              ))}
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => createProduct(createProductPayload)}
          size="large"
        >
          {saveEnabled ? 'Create Product' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateProductModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
